import { REPORT } from '@consts/reports';
import { useReportLoad } from '@hooks/useReportLoad';
import { BaseReport } from '../BaseReport';
import { average, countUnique, sum } from '@utils/summaries';
import { getUniqueDates, groupByKeys, seriesMap, groupBy } from '@utils/data';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';

interface FullBodyPctByResellerProps {
    dateRange: { startDate: string; endDate: string };
}

export const FullBodyPctByReseller = ({ dateRange }: FullBodyPctByResellerProps) => {
    const [range, setRange] = useRange('FullBodyPctByReseller');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.RESELLER_FULL_BODY_PERCENTAGE_RATE_PER_RESELLER,
        params: { ...dateRange, range },
    });

    const globals = groupBy(data, 'date');

    const resellers = groupByKeys(data, ['resellerName', 'date']);

    const dateKeys = getUniqueDates(data, range);

    const globalAvgSeries = {
        visible: true,
        name: 'Global Average',
        data: dateKeys.map(({ date }) => globals[date]?.[0]?.globalAvg || 0),
    };

    const globalMaxSeries = {
        visible: true,
        name: 'Global Highmark',
        data: dateKeys.map(({ date }) => globals[date]?.[0]?.globalMax || 0),
    };

    const resellersSeries = seriesMap({
        items: resellers,
        valueKey: 'bodyPct',
        visible: false,
        dateKeys,
    });

    return (
        <BaseReport
            title={'Full Body % Rate by Reseller'}
            description={'This graph shows you the rate at which each of your Resellers cuts Full Body coverage. '}
            note={
                'Note: not all of Resellers are visible, as some are deselected by default. You can select or deselect Resellers to display on the graph by clicking on them in the legend below.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            range={range}
            setRange={setRange}
            tableOptions={{
                headers: ['Date', 'Reseller Name', 'Body %', 'Global Avg', 'Global Max'],
                data: data.map((item) => [item.date, item.resellerName, item.bodyPct, item.globalAvg, item.globalMax]),
                summaries: [countUnique, countUnique, average, average, sum],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(500, [{ name: 'Body Percentage', opposite: false }], dateKeys, [
                globalAvgSeries,
                globalMaxSeries,
                ...resellersSeries,
            ])}
        />
    );
};
